import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { publicApi } from "@/lib/api";
import { BRAND } from "@/lib/brand";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, bookingRef: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, paid: false, bookingRef: null });
      return;
    }
    let attempts = 0;
    let cancelled = false;
    async function poll() {
      try {
        const { data } = await publicApi.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ loading: false, paid: true, bookingRef: data.booking_ref });
          return;
        }
        if (attempts++ < 15) {
          setTimeout(poll, 2000);
        } else {
          setState({ loading: false, paid: false, bookingRef: data.booking_ref });
        }
      } catch (e) {
        if (attempts++ < 5) setTimeout(poll, 2000);
        else setState({ loading: false, paid: false, bookingRef: null });
      }
    }
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <main data-testid="booking-success-page" className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
      <div className="max-w-lg w-full text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-10">
          <img src={BRAND.logoUrl} alt="MHJ" className="h-10 w-10 object-contain" />
          <span className="font-display font-extrabold text-sm tracking-tight">MHJ PRESTIGE</span>
        </Link>

        {state.loading ? (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-[#E60000] mx-auto" />
            <div className="mt-6 text-sm tracking-widest uppercase text-zinc-400">
              Confirming your payment…
            </div>
          </>
        ) : state.paid ? (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 border border-[#E60000] mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-[#E60000]" />
            </div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
              Booking Secured
            </div>
            <h1 className="font-display font-extrabold text-3xl md:text-5xl mt-4">
              Thank you.
            </h1>
            <p className="text-zinc-400 mt-4">
              Thanks for choosing MHJ Prestige Transport. We&apos;ll be in touch
              shortly with your transport confirmation.
            </p>
            {state.bookingRef && (
              <div className="mt-8 border border-white/10 bg-[#0A0A0A] p-6">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
                  Booking reference
                </div>
                <div
                  className="font-display font-extrabold text-2xl md:text-3xl mt-2"
                  data-testid="booking-ref"
                >
                  {state.bookingRef}
                </div>
                <a
                  href={`/track`}
                  className="text-[#E60000] text-xs tracking-widest uppercase mt-4 inline-block hover:text-white"
                  data-testid="booking-success-track-link"
                >
                  Track this booking →
                </a>
              </div>
            )}
            <Link
              to="/"
              className="btn-ghost mt-10 justify-center inline-flex"
              data-testid="booking-success-home"
            >
              Return home <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        ) : (
          <>
            <h1 className="font-display font-extrabold text-3xl md:text-4xl">
              Payment status pending
            </h1>
            <p className="text-zinc-400 mt-4">
              We&apos;re still confirming your payment. Please refresh this page
              in a moment, or call us on {BRAND.phone}.
            </p>
            <Link to="/" className="btn-ghost mt-8 justify-center inline-flex">
              Return home
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
